import { useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import backend from "../services/backend";
import AuthGuard from "./AuthGuard";
import { Button } from "../catalyst/button";
import { ConfirmDialog } from "./ConfirmDialog";

const depthDescriptions: Record<1 | 2 | 3 | 4, string> = {
  1: "classification data",
  2: "embeddings and classification data",
  3: "text, embeddings, and classification data",
  4: "pages, text, embeddings, and classification data",
};

export function ServerStatusAdmin() {
  const [isResetting, setIsResetting] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDepth, setResetDepth] = useState<1 | 2 | 3 | 4>(1);
  const [isRetryingFailedSteps, setIsRetryingFailedSteps] = useState(false);

  const handleReset = async () => {
    try {
      setIsResetting(true);
      setResetError(null);
      await backend.resetDocuments(resetDepth);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : "Failed to reset documents");
    } finally {
      setIsResetting(false);
      setIsResetConfirmOpen(false);
    }
  };

  const handleRetryFailedSteps = async () => {
    setIsRetryingFailedSteps(true);
    backend.retryAllFailedSteps().finally(() => {
      setIsRetryingFailedSteps(false);
    });
  };

  return (
    <AuthGuard role="admin">
      <div className="space-y-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm dark:border-amber-800 dark:bg-amber-950/40">
          <div className="mb-4 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <h3 className="text-lg font-medium text-amber-950 dark:text-amber-100">
              Admin — maintenance
            </h3>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                Retry failed pipeline steps
              </h4>
              <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
                Re-queue failed processing steps across all documents.
              </p>
            </div>
            <Button
              color="amber"
              onClick={handleRetryFailedSteps}
              disabled={isRetryingFailedSteps}
              className="shrink-0"
            >
              {isRetryingFailedSteps ? "Retrying…" : "Retry failed steps"}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm dark:border-red-900 dark:bg-red-950/30">
          <div className="mb-4 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
            <h3 className="text-lg font-medium text-red-950 dark:text-red-100">Admin — danger zone</h3>
          </div>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h4 className="text-sm font-medium text-red-900 dark:text-red-100">Reset documents</h4>
                <p className="mt-1 text-sm text-red-800/90 dark:text-red-300/90">
                  Clears stored processing outputs and triggers reprocessing. Higher depths include
                  everything below them.
                </p>
              </div>
              <fieldset className="space-y-2">
                {(
                  [
                    [1, "Depth 1 — classification only"],
                    [2, "Depth 2 — embeddings and classification"],
                    [3, "Depth 3 — extracted text, embeddings, and classification"],
                    [4, "Depth 4 — pages, text, embeddings, and classification"],
                  ] as const
                ).map(([depth, label]) => (
                  <label key={depth} className="flex cursor-pointer items-start gap-2">
                    <input
                      type="radio"
                      name="resetDepth"
                      value={depth}
                      checked={resetDepth === depth}
                      onChange={() => setResetDepth(depth)}
                      className="mt-1 h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm text-red-900 dark:text-red-100">{label}</span>
                  </label>
                ))}
              </fieldset>
            </div>
            <Button
              color="red"
              onClick={() => setIsResetConfirmOpen(true)}
              disabled={isResetting}
              className="shrink-0"
            >
              {isResetting ? "Resetting…" : "Reset documents"}
            </Button>
          </div>
          {resetError ? (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400">{resetError}</p>
          ) : null}
        </div>

        <ConfirmDialog
          open={isResetConfirmOpen}
          title="Reset Documents"
          message={`Are you sure you want to reset ${depthDescriptions[resetDepth]} for all documents? This action cannot be undone.`}
          confirmLabel="Reset"
          pendingLabel="Resetting…"
          isPending={isResetting}
          onConfirm={() => void handleReset()}
          onClose={() => setIsResetConfirmOpen(false)}
        />
      </div>
    </AuthGuard>
  );
}
